import { getLineTaxableBase, roundMoney } from "./billAmounts";

export type SaleLineProduct = {
  price?: number | string | null;
  lineDiscount?: number | string | null;
  quantity?: number | string | null;
  tax?: number | string | null;
  "TaxHistory.value"?: number | string | null;
  "TaxHistory.isPercentage"?: boolean | null;
};

/** Tax rate (%) stored on the sale line via TaxHistory at checkout. */
export function getItemTaxRatePercent(product: SaleLineProduct): number {
  const historyValue = Number(product["TaxHistory.value"] ?? 0);
  const isPercentage = product["TaxHistory.isPercentage"] !== false;

  if (historyValue > 0 && isPercentage) {
    return historyValue;
  }

  const legacyTax = Number(product.tax ?? 0);
  return legacyTax > 0 ? legacyTax : 0;
}

/**
 * Same rule as POS checkout:
 * - item tax rate when the product has one
 * - otherwise mart default tax when the bill has tax left to account for
 */
export function computeSaleLineTaxes(
  products: SaleLineProduct[],
  billTax: number | string | null | undefined,
  defaultTaxPercent: number | string | null | undefined,
): number[] {
  const billTaxTotal = roundMoney(Number(billTax || 0));
  if (products.length === 0) {
    return [];
  }

  const bases = products.map(getLineTaxableBase);
  const itemRates = products.map(getItemTaxRatePercent);

  const lineTaxes = products.map((_product, index) => {
    const rate = itemRates[index];
    if (rate > 0) {
      return roundMoney(bases[index] * (rate / 100));
    }
    return 0;
  });

  const itemTaxSum = roundMoney(lineTaxes.reduce((sum, amount) => sum + amount, 0));
  const remainingTax = roundMoney(billTaxTotal - itemTaxSum);
  const defaultRate = Number(defaultTaxPercent || 0);

  if (remainingTax <= 0 || defaultRate <= 0) {
    return lineTaxes;
  }

  const untaxedLineIndexes = itemRates
    .map((rate, index) => (rate <= 0 ? index : -1))
    .filter((index) => index >= 0);

  if (untaxedLineIndexes.length === 0) {
    return lineTaxes;
  }

  untaxedLineIndexes.forEach((index) => {
    lineTaxes[index] = roundMoney(bases[index] * (defaultRate / 100));
  });

  const computedTotal = roundMoney(lineTaxes.reduce((sum, amount) => sum + amount, 0));
  const roundingDiff = roundMoney(billTaxTotal - computedTotal);
  if (roundingDiff !== 0) {
    const adjustIndex = untaxedLineIndexes[untaxedLineIndexes.length - 1];
    lineTaxes[adjustIndex] = roundMoney(lineTaxes[adjustIndex] + roundingDiff);
  }

  return lineTaxes;
}

/** Line subtotal after line discount, including line tax. */
export function getLineSubtotalIncTax(
  product: SaleLineProduct,
  lineTaxAmount: number,
): number {
  return roundMoney(getLineTaxableBase(product) + lineTaxAmount);
}
